// ============================================================================
// components/remote/RemotePanel.tsx — ПОЛНОЭКРАННЫЙ экран сеанса «Удалённого
// доступа» (viewer — смотрю/управляю; host — показываю экран).
// Транспорт: LiveKit-комната remote_{sessionId}; трек — ScreenShare.
// Управление — useRemoteControl (Ф2); файлы устройства — useRemoteFiles (Ф3);
// панель действий — RemoteActionBar; просмотр — RemoteScreen.
// Контракт: plans/remote-protocol-contract.md.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { FolderOpen, Loader2, Maximize2, Monitor, MousePointerClick, ShieldCheck } from 'lucide-react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { useToastStore } from '@/store/toastStore';
import { getExternalBaseUrl } from '@/lib/wailsBridge';
import { copyToClipboard } from '@/lib/clipboard';
import { eventBusHub } from '@/lib/eventBusHub';
import { logEvent } from '@/lib/eventLog';
import {
  captureScreenStream,
  publishRemoteSignal,
  remotePreset,
  remoteRoomName,
  remoteTopic,
  type RemoteRole,
} from '@/lib/remote/remoteSession';
import { RemoteScreen } from '@/components/remote/RemoteScreen';
import { RemoteActionBar, type RemoteStatus } from '@/components/remote/RemoteActionBar';
import { RemoteFilesPanel } from '@/components/remote/RemoteFilesPanel';
import { useRemoteControl } from '@/components/remote/useRemoteControl';
import { useRemoteFiles } from '@/components/remote/useRemoteFiles';
import { useRemoteStats, type RemoteStats } from '@/components/remote/useRemoteStats';

interface RemotePanelProps {
  sessionId: string;
  role: RemoteRole;
  hostName?: string;
  /** Поток, захваченный в RemoteOverlay по клику (user gesture) — для host. */
  initialStream?: MediaStream | null;
  /** Готовые LiveKit-креды (гостевой просмотр — Ф4); иначе берутся через media. */
  livekit?: { url: string; token: string };
  /** Источник сеанса для истории (app|chat|guest) — Ф4 сессия-объект. */
  source?: string;
  onLeave: () => void;
}

export function RemotePanel({ sessionId, role, hostName, initialStream, livekit, source, onLeave }: RemotePanelProps) {
  const myId = useAuthStore((s) => s.user?.id || '');
  const myName = useAuthStore((s) => s.user?.name || '');
  const [status, setStatus] = useState<RemoteStatus>('connecting');
  const [error, setError] = useState('');
  const [sharing, setSharing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [filesPanelOpen, setFilesPanelOpen] = useState(false);
  const [preset, setPreset] = useState('auto');
  const roomRef = useRef<Room | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ownedStreamRef = useRef(false);
  const endedHandlerRef = useRef<(() => void) | null>(null);
  const leftRef = useRef(false);
  const startedAtRef = useRef(0);
  const statsRef = useRef<RemoteStats>({});
  const onLeaveRef = useRef(onLeave);
  useEffect(() => {
    onLeaveRef.current = onLeave;
  }, [onLeave]);

  const control = useRemoteControl({ roomRef, role, sessionId, stageRef, active: true });
  const files = useRemoteFiles({ roomRef, role, sessionId, active: true });
  const stats = useRemoteStats(roomRef, sessionId, role, true);
  useEffect(() => {
    statsRef.current = stats;
  }, [stats]);

  const log = useCallback((level: 'info' | 'error', message: string, details?: string) => {
    void logEvent({
      level,
      category: 'ui',
      message,
      entityType: 'remote',
      entityCode: 'session',
      objectId: sessionId,
      details: details || '',
    });
  }, [sessionId]);

  const finishLocal = useCallback((message: string, level: 'info' | 'error' = 'info') => {
    if (leftRef.current) return;
    leftRef.current = true;
    log(level, message);
    // Ф4: сессия как объект платформы — фиксируем завершение (гость не вызывает).
    if (myId && startedAtRef.current) {
      const durationSec = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000));
      startedAtRef.current = 0;
      getSDK().remote.endRemoteSession({ sessionId, durationSec, quality: JSON.stringify(statsRef.current) })
        .catch((e: unknown) => log('error', 'удалённый доступ: не удалось записать завершение сессии', String((e as Error)?.message || e)));
    }
    const room = roomRef.current;
    roomRef.current = null;
    const w = window as unknown as { __hydraRemoteRoom?: Room };
    if (w.__hydraRemoteRoom === room) delete w.__hydraRemoteRoom;
    if (room) {
      try {
        room.disconnect();
      } catch {
        /* уже отключены */
      }
    }
    if (ownedStreamRef.current && streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    streamRef.current = null;
    ownedStreamRef.current = false;
    onLeaveRef.current();
  }, [log, myId, sessionId]);

  const stopOwnedStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      if (endedHandlerRef.current) {
        stream.getVideoTracks().forEach((t) => t.removeEventListener('ended', endedHandlerRef.current as () => void));
      }
      if (ownedStreamRef.current) stream.getTracks().forEach((t) => t.stop());
    }
    endedHandlerRef.current = null;
    streamRef.current = null;
    ownedStreamRef.current = false;
  }, []);

  const stopSharing = useCallback(async (note: string) => {
    const room = roomRef.current;
    const stream = streamRef.current;
    if (room && stream) {
      try {
        await room.localParticipant.unpublishTrack(stream.getVideoTracks()[0], true);
      } catch (e) {
        log('error', 'удалённый доступ: не удалось остановить показ', String((e as Error)?.message || e));
      }
    }
    stopOwnedStream();
    setSharing(false);
    if (!leftRef.current) setStatus('waiting');
    log('info', `удалённый доступ: ${note}`);
  }, [log, stopOwnedStream]);

  const publishScreen = useCallback(async (stream: MediaStream) => {
    const room = roomRef.current;
    if (!room) throw new Error('нет соединения с сеансом');
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error('в потоке нет видеодорожки');
    const q = remotePreset(preset);
    await room.localParticipant.publishTrack(track, {
      source: Track.Source.ScreenShare,
      videoEncoding: { maxBitrate: q.maxBitrate, maxFramerate: q.fps },
    });
    setSharing(true);
    setStatus('live');
    log('info', 'удалённый доступ: показ экрана начат', JSON.stringify({ ...track.getSettings(), preset: q.code, maxBitrate: q.maxBitrate }));
  }, [log, preset]);

  const attachEnded = useCallback((stream: MediaStream) => {
    const track = stream.getVideoTracks()[0];
    const onEnded = () => {
      void stopSharing('показ экрана остановлен системой');
    };
    endedHandlerRef.current = onEnded;
    track?.addEventListener('ended', onEnded);
  }, [stopSharing]);

  const shareNow = useCallback(async () => {
    try {
      const stream = await captureScreenStream(remotePreset(preset));
      streamRef.current = stream;
      ownedStreamRef.current = true;
      attachEnded(stream);
      await publishScreen(stream);
    } catch (e) {
      const msg = String((e as Error)?.message || e);
      setError(msg);
      log('error', 'удалённый доступ: не удалось начать показ экрана', msg);
    }
  }, [attachEnded, log, publishScreen, preset]);

  // R1: смена пресета — fps применяется сразу (applyConstraints), битрейт и
  // разрешение — при следующем начале показа (WebRTC требует republish).
  const applyPreset = useCallback((code: string) => {
    setPreset(code);
    const q = remotePreset(code);
    const t = streamRef.current?.getVideoTracks()[0];
    if (t) {
      t.applyConstraints({ frameRate: q.fps }).catch((e: unknown) => {
        log('error', 'удалённый доступ: не удалось применить пресет (fps)', String((e as Error)?.message || e));
      });
    }
    log('info', 'удалённый доступ: пресет качества', JSON.stringify({ code: q.code, fps: q.fps, maxBitrate: q.maxBitrate }));
  }, [log]);

  // Сигналы завершения/отказа от ВТОРОЙ стороны
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      if (ev.payload?.user_id === myId) return;
      if (ev.topic === remoteTopic.declined(sessionId)) {
        finishLocal('удалённый доступ: сеанс отклонён второй стороной');
      } else if (ev.topic === remoteTopic.ended(sessionId)) {
        finishLocal('удалённый доступ: сеанс завершён второй стороной');
      }
    });
    return unsub;
  }, [sessionId, myId, finishLocal]);

  // Подключение к комнате + публикация (host) / ожидание экрана (viewer)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let url = '';
        let token = '';
        if (livekit) {
          url = livekit.url;
          token = livekit.token;
        } else {
          const r: { url?: string; token?: string } = await getSDK().media.createLiveKitToken({ room: remoteRoomName(sessionId) } as never);
          url = String(r?.url || '');
          token = String(r?.token || '');
        }
        if (!url || !token) throw new Error('Удалённый доступ недоступен (LiveKit не настроен)');
        if (cancelled) return;

        const room = new Room();
        roomRef.current = room;
        (window as unknown as { __hydraRemoteRoom?: Room }).__hydraRemoteRoom = room;

        room.on(RoomEvent.Disconnected, () => {
          if (!cancelled && !leftRef.current) finishLocal('удалённый доступ: соединение закрыто');
        });
        room.on(RoomEvent.ParticipantDisconnected, () => {
          if (!cancelled && role === 'viewer') finishLocal('удалённый доступ: собеседник отключился');
        });

        await room.connect(url, token);
        if (cancelled) return;
        log('info', 'удалённый доступ: подключение к сеансу', JSON.stringify({ role }));

        // Ф4: сессия как объект платформы (гость не вызывает — фиксирует host).
        startedAtRef.current = Date.now();
        if (myId) {
          getSDK().remote.startRemoteSession({
            sessionId,
            role,
            room: remoteRoomName(sessionId),
            source: source || 'app',
            myName,
            peerId: '',
            peerName: hostName || '',
          }).catch((e: unknown) => log('error', 'удалённый доступ: не удалось зафиксировать сессию', String((e as Error)?.message || e)));
        }

        if (role === 'host') {
          const initial = initialStream || null;
          if (initial) {
            streamRef.current = initial;
            attachEnded(initial);
            await publishScreen(initial);
          } else {
            setStatus('waiting');
          }
        } else {
          setStatus('waiting');
        }
      } catch (e) {
        if (cancelled) return;
        const msg = String((e as Error)?.message || e);
        setError(msg);
        setStatus('error');
        log('error', 'удалённый доступ: ошибка подключения', msg);
      }
    })();
    return () => {
      cancelled = true;
      leftRef.current = true;
      const room = roomRef.current;
      roomRef.current = null;
      const w = window as unknown as { __hydraRemoteRoom?: Room };
      if (w.__hydraRemoteRoom === room) delete w.__hydraRemoteRoom;
      if (room) {
        try {
          room.disconnect();
        } catch {
          /* ignore */
        }
      }
      if (ownedStreamRef.current && streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      streamRef.current = null;
      ownedStreamRef.current = false;
    };
    // initialStream/колбэки стабильны на время сеанса; пересоздание — по id/роли
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, role, source]);

  const leave = useCallback(() => {
    // Гость (без авторизации) не может публиковать сигналы шины — просто закрывает.
    if (myId) {
      publishRemoteSignal(remoteTopic.ended(sessionId), { user_id: myId, name: myName });
    }
    finishLocal('удалённый доступ: сеанс завершён (я)');
  }, [sessionId, myId, myName, finishLocal]);

  // Ф4: гостевая ссылка /remote/{slug}/{token} (host) — копируется в буфер.
  const [guestBusy, setGuestBusy] = useState(false);
  const createGuestLink = useCallback(async () => {
    if (!myId) return;
    setGuestBusy(true);
    try {
      const r: { token?: string } = await getSDK().remote.createRemoteGuestLink({ sessionId, ttlMinutes: 60 });
      if (!r?.token) throw new Error('сервер не вернул токен ссылки');
      const slug = useCenterStore.getState().baseSlug || '';
      if (!slug) throw new Error('база не определена');
      const origin = await getExternalBaseUrl().catch(() => window.location.origin);
      await copyToClipboard(`${origin}/remote/${slug}/${r.token}`);
      useToastStore.getState().showSuccess('Ссылка для гостя скопирована (действует 60 минут)');
      log('info', 'удалённый доступ: создана гостевая ссылка', JSON.stringify({ ttlMinutes: 60 }));
    } catch (e) {
      const msg = String((e as Error)?.message || e);
      useToastStore.getState().showError(`Не удалось создать ссылку: ${msg}`);
      log('error', 'удалённый доступ: ошибка создания гостевой ссылки', msg);
    } finally {
      setGuestBusy(false);
    }
  }, [sessionId, myId, log]);

  const toggleFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      setFullscreen(false);
    } else {
      void el.requestFullscreen();
      setFullscreen(true);
    }
  }, []);

  const title = role === 'host' ? 'Вы показываете свой экран' : `Просмотр экрана${hostName ? ` — ${hostName}` : ''}`;
  const statusText = status === 'connecting'
    ? 'Подключение…'
    : status === 'live'
      ? (role === 'host' ? 'Экран передаётся' : 'Экран транслируется')
      : status === 'error'
        ? (error || 'Ошибка сеанса')
        : (role === 'host' ? 'Показ экрана не начат' : 'Ожидание экрана…');

  return (
    <div className="fixed inset-0 z-[12500] flex flex-col bg-black/95 text-white">
      <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
        <Monitor className="h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{title}</div>
          <div className="truncate text-[11px] text-white/60">{statusText}</div>
        </div>
        {(stats.rttMs !== undefined || stats.fps !== undefined || stats.kbps !== undefined) && (
          <span className="hidden shrink-0 items-center gap-1 font-mono text-[10px] text-white/50 md:flex">
            {stats.rttMs !== undefined && <span>{stats.rttMs}мс</span>}
            {stats.fps !== undefined && <span>· {stats.fps}fps</span>}
            {stats.kbps !== undefined && <span>· {(stats.kbps / 1000).toFixed(1)}Мбит/с</span>}
            {stats.width ? <span>· {stats.width}×{stats.height}</span> : null}
          </span>
        )}
        {role === 'viewer' && control.controlOn && (
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-green-600/20 px-2.5 py-1 text-[11px] text-green-300">
            <MousePointerClick className="h-3.5 w-3.5" />
            Управление включено
          </span>
        )}
        {role === 'host' && control.controlOn && (
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500/20 px-2.5 py-1 text-[11px] text-amber-300">
            <ShieldCheck className="h-3.5 w-3.5" />
            Управляют вами{control.inputPackets > 0 ? ` · ${control.inputPackets}` : ''}
          </span>
        )}
        {role === 'host' && files.filesOn && (
          <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-sky-500/20 px-2.5 py-1 text-[11px] text-sky-300">
            <FolderOpen className="h-3.5 w-3.5" />
            Файлы открыты
          </span>
        )}
        {role === 'viewer' && (
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={fullscreen ? 'Выйти из полного экрана' : 'На весь экран'}
            className="rounded-full p-2 hover:bg-white/10"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div ref={stageRef} className={`relative min-h-0 flex-1 ${role === 'viewer' && control.controlOn ? 'cursor-crosshair' : ''}`}>
        {role === 'viewer' ? (
          <RemoteScreen onState={(ready) => setStatus((cur) => (cur === 'error' ? cur : ready ? 'live' : 'waiting'))} />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-white/70">
            {sharing ? (
              <Monitor className="h-10 w-10 animate-pulse text-primary" />
            ) : (
              <Monitor className="h-10 w-10" />
            )}
            <span className="text-sm">{sharing ? 'Ваш экран виден собеседнику' : 'Показ экрана не начат'}</span>
          </div>
        )}
        {status === 'connecting' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 px-6">
            <div className="max-w-md text-center text-sm text-red-300">{error || 'Не удалось подключиться к сеансу'}</div>
          </div>
        )}
        {role === 'viewer' && filesPanelOpen && files.filesOn && (
          <RemoteFilesPanel files={files} onClose={() => setFilesPanelOpen(false)} />
        )}
      </div>

      <RemoteActionBar
        role={role}
        status={status}
        sharing={sharing}
        control={control}
        files={files}
        filesPanelOpen={filesPanelOpen}
        onToggleFilesPanel={() => setFilesPanelOpen((v) => !v)}
        preset={preset}
        onPresetChange={applyPreset}
        onShareNow={() => void shareNow()}
        onStopShare={() => void stopSharing('показ экрана остановлен')}
        onGuestLink={() => void createGuestLink()}
        guestLinkBusy={guestBusy}
        onLeave={leave}
      />
    </div>
  );
}
